import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Project } from '../../types';
import apiService from '../../services/api';
import type { ProjectListFilterParams, ProjectStatsResponse } from '../../services/api';

interface ProjectsState {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  filters: {
    status: string;
    priority: string;
    clientId: string;
    type: string;
    search: string;
  };
  /** Overview aggregates over the FULL dataset matching the current filters (not the page). */
  stats: ProjectStatsResponse['overview'] | null;
}

const initialState: ProjectsState = {
  projects: [],
  currentProject: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 1000, // Show all projects without pagination
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
  filters: {
    status: 'all',
    priority: 'all',
    clientId: 'all',
    type: 'all',
    search: '',
  },
  stats: null,
};

export const fetchProjects = createAsyncThunk(
  'projects/fetchProjects',
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getProjects(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch projects');
    }
  }
);

export const fetchProject = createAsyncThunk(
  'projects/fetchProject',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiService.getProject(id);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch project');
    }
  }
);

export const createProject = createAsyncThunk(
  'projects/createProject',
  async (projectData: Partial<Project>, { rejectWithValue }) => {
    try {
      const response = await apiService.createProject(projectData);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create project');
    }
  }
);

export const updateProject = createAsyncThunk(
  'projects/updateProject',
  async ({ id, data }: { id: string; data: Partial<Project> }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateProject(id, data);
      return response.result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update project');
    }
  }
);

export const deleteProject = createAsyncThunk(
  'projects/deleteProject',
  async (id: string, { rejectWithValue }) => {
    try {
      await apiService.deleteProject(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete project');
    }
  }
);

/** Pass the same filters as `fetchProjects` so the tiles describe exactly the listed projects. */
export const fetchProjectStats = createAsyncThunk(
  'projects/fetchProjectStats',
  async (params: ProjectListFilterParams = {}, { rejectWithValue }) => {
    try {
      const response = await apiService.getProjectStats(params);
      return response.data.overview;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch project stats');
    }
  }
);

const projectsSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentProject: (state, action) => {
      state.currentProject = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: 'all',
        priority: 'all',
        clientId: 'all',
        type: 'all',
        search: '',
      };
    },

  },
  extraReducers: (builder) => {
    builder
      // Fetch projects
      .addCase(fetchProjects.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as any;
        // Backend returns `results` (see PaginatedResponse); fall back to `data` for safety.
        state.projects = (payload.results ?? payload.data ?? []) as Project[];
        state.pagination = payload.pagination;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch single project
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.currentProject = action.payload as Project;
      })
      // Create project
      .addCase(createProject.fulfilled, (state, action) => {
        state.projects.unshift(action.payload as Project);
      })
      // Update project
      .addCase(updateProject.fulfilled, (state, action) => {
        const updatedProject = action.payload as Project;
        const index = state.projects.findIndex(p => p.id === updatedProject.id);
        if (index !== -1) {
          state.projects[index] = updatedProject;
        }
        if (state.currentProject?.id === updatedProject.id) {
          state.currentProject = updatedProject;
        }
      })
      // Delete project
      .addCase(deleteProject.fulfilled, (state, action) => {
        const projectId = action.payload as string;
        state.projects = state.projects.filter(p => p.id !== projectId);
        if (state.currentProject?.id === projectId) {
          state.currentProject = null;
        }
      })
      // Fetch project stats
      .addCase(fetchProjectStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(fetchProjectStats.rejected, (state) => {
        state.stats = null;
      });
  },
});

export const { clearError, setCurrentProject, setFilters, clearFilters } = projectsSlice.actions;
export default projectsSlice.reducer;
